import _ from 'lodash';
import { ChangeEvent, FormEvent, useState } from 'react';
import { Button, Link, Stack, Typography } from '@common-components';
import { useBulkCreateContacts } from 'hooks';
import { BulkCreateError, Contact, Team } from 'types';
import { Dialog, DialogContent, DialogHeader } from 'components/Dialog';
import InternalLink from 'components/InternalLink';
import adminRoutes from 'admin/admin-routes';

interface ContactsBulkUploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  team: Team;
}

export default function ContactsBulkUploadDialog({ isOpen, onClose, team }: ContactsBulkUploadDialogProps) {
  const { bulkCreateContacts } = useBulkCreateContacts();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [updatedUserMarketRows, setUpdatedUserMarketRows] = useState<Array<Contact> | null>(null);
  const [errors, setErrors] = useState<BulkCreateError[]>([]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    const response = await bulkCreateContacts.mutateAsync({
      data: {
        file: selectedFile!,
        teamId: team.id,
      },
    });
    if (response) {
      setUpdatedUserMarketRows(response.created);

      setErrors(response.errors);
    }
    setIsSubmitting(false);
    setSelectedFile(null);
  };

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    setUpdatedUserMarketRows(null);
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const closeDialog = () => {
    setIsSubmitting(false);
    setSelectedFile(null);
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={closeDialog}
      aria-labelledby="contacts-bulk-upload"
      fullScreen
      sx={{ height: 1 }}
      muiPaperProps={{ sx: { bgcolor: 'common.white' } }}
    >
      <DialogHeader title="Upload Contacts From CSV" id="contactsBulkUpload" onClose={closeDialog} />

      <DialogContent sx={{ bgcolor: 'grey.100', height: 1 }}>
        <Typography variant="h5">{team.name}</Typography>
        <form onSubmit={onSubmit}>
          <Stack mt={3} direction="column" gap={5}>
            <input type="file" onChange={handleFileSelect} />
            <Link
              href="https://docs.google.com/spreadsheets/d/1C9TWsheImPgSEkiKlF7Xq4ssURYKRyX6yoL-p0LnJaA/edit?usp=sharing"
              target="_blank"
            >
              Example File
            </Link>
            <Button loading={isSubmitting} onClick={onSubmit} disabled={!selectedFile} variant="contained">
              Upload File
            </Button>
          </Stack>
        </form>
        {updatedUserMarketRows !== null && (
          <Stack mt={3} gap={1}>
            <Typography color="green.400" variant="subtitle1">{`File uploaded successfully (${
              updatedUserMarketRows.length
            } Markets , ${_.uniqBy(updatedUserMarketRows, 'email').length} contacts)`}</Typography>

            <Typography color="red" variant="subtitle1">{`${errors.length} contacts failed due to errors`}</Typography>
            <InternalLink to={adminRoutes.getContactByTeamId(team.id)}>go to team contacts</InternalLink>
            {errors.length > 0 && (
              <>
                <Typography variant="h5" pt={5}>
                  Errors:
                </Typography>
                <Stack mt={3} gap={1}>
                  {errors.map((error) => (
                    <Typography key={error.rowNumber} variant="body2">
                      Line {error.rowNumber}: {error.error}
                    </Typography>
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
